/**
 * Text messages at the seam (piece 21; decision 0312): the status and error
 * vocabularies the plan defines, and the idempotency key composed from what
 * is sent rather than from the request, so a send that times out and is
 * retried produces one message.
 */
export const MESSAGE_STATUSES = ['Queued', 'Sent', 'Delivered', 'Failed', 'Undeliverable', 'Unknown'] as const;
export type MessageStatusName = (typeof MESSAGE_STATUSES)[number];

export const MESSAGE_ERROR_CODES = ['RecipientUnsubscribed', 'InvalidNumber', 'CarrierRejected', 'RateLimited', 'ProviderUnavailable'] as const;
export type MessageErrorCodeName = (typeof MESSAGE_ERROR_CODES)[number];

/** The record types a message is sent about, the TaggedRecord pair flattened into two columns (piece 21). */
export const MESSAGE_RELATED_RECORD_TYPES = ['WorkOrder', 'Invoice', 'Bill', 'Quote', 'SalesOrder', 'PurchaseOrder', 'Deal', 'Lead', 'CompanyRecord', 'PersonRecord', 'SiteRecord', 'Event', 'AccessInvitation'] as const;
export type MessageRelatedRecordType = (typeof MESSAGE_RELATED_RECORD_TYPES)[number];

export interface MessageIdentity {
  recordType: MessageRelatedRecordType;
  recordId: string;
  /** The document's revision where the subject is one; 1 for a row sent once. */
  revision: number | string;
  channel: 'Sms';
  recipient: string;
}

/** `{recordType}:{recordId}:{revision}:{channel}:{recipient}`, the recipient with its spacing and punctuation stripped so one number composes one key. */
export function messageIdempotencyKey(identity: MessageIdentity): string {
  const recipient = identity.recipient.replace(/[\s().-]/g, '');
  return `${identity.recordType}:${identity.recordId}:${identity.revision}:${identity.channel}:${recipient}`;
}

/** Hours after which a Sent message with no receipt is Unknown rather than Sent forever (piece 21). */
export const MESSAGE_RECEIPT_HOURS = 48;

/** 🔒 Piece 21, decision 10710: the nightly job that ages a text with no receipt past that window to Unknown, under its own hour key, seeded at 4. */
export const SWEEP_UNANSWERED_TEXTS_JOB = 'sweepUnansweredTexts';
export const SWEEP_UNANSWERED_TEXTS_HOUR_KEY = 'messaging.receiptSweepRunHour';
export const SWEEP_UNANSWERED_TEXTS_DEFAULT_HOUR = 4;
